import { useRef, useEffect } from 'react';
import workspace from "./workspace";
import sqftEventManager from "../store/sqftEventManager";
import sqftShapeManager from "../store/sqftShapeManager";

const SqftUseCanvas = (
  renderEvents,
  options = {},
  visualizersLoadedLength,
  fabricatorData,
  sqftVisited,
  intl,
  quotes,
  activeQuoteIndex,
  getTargetSize // () => { width, height } in CSS pixels
) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !getTargetSize) return;

    const ctx = canvas.getContext(options.context || "2d");

    // Get logical size (CSS pixels)
    const { width, height } = getTargetSize();

    // Use stable dpr, never less than 1
    const baseDpr = window.devicePixelRatio || 1;
    const dpr = Math.max(1, baseDpr);

    const cssW = Math.max(1, Math.round(width));
    const cssH = Math.max(1, Math.round(height));
    const pxW = Math.max(1, Math.round(cssW * dpr));
    const pxH = Math.max(1, Math.round(cssH * dpr));

    // Set CSS size
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;

    // Set backing store size
    canvas.width = pxW;
    canvas.height = pxH;

    // Normalize coordinates to logical pixels
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    if (typeof workspace.onResize === "function") {
      // Pass logical size + dpr
      workspace.onResize({ width: cssW, height: cssH, dpr });
    }
  }, [options.context, getTargetSize]);

  useEffect(() => {
    const updateRate = 1000 / 60;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext(options.context || "2d");

    let frameCount = 0;
    let rafId;

    const requestAnimFrame = (render) =>
      window.requestAnimationFrame(render) ||
      window.webkitRequestAnimationFrame(render) ||
      window.mozRequestAnimationFrame(render) ||
      window.oRequestAnimationFrame(render) ||
      window.msRequestAnimationFrame(render) ||
      function (render) {
        window.setTimeout(render, updateRate);
      };

    const render = () => {
      frameCount++;

      // sort render events by priority
      renderEvents.sort((a, b) => parseFloat(b.priority) - parseFloat(a.priority));
      renderEvents.forEach((task) => task.render.call(null, ctx, frameCount));

      // render shapes
      sqftShapeManager.shapes.forEach((shape) => {
        shape.render.call(null, ctx);
      });

      // trigger custom render event
      sqftEventManager.trigger(sqftEventManager.events.RENDER);
      rafId = requestAnimFrame(render);
    };

    render();

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [renderEvents, options.context]);

  return canvasRef;
};

export default SqftUseCanvas;
